import React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ViewSentApplicationInst.css";
import Footer from "./components/Footer";
import Header from "./components/Header";

const params = new URLSearchParams(window.location.search);
const isEmail = params.get('source') === 'email';

const API_BASE_URL = isEmail
  ? 'http://127.0.0.1:5000'          // Puppeteer on server → localhost
  : 'http://180.211.162.102:5000'; 

const ViewSentApplicationInst = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Get auth and institute data
  const [auth, setAuth] = useState(null);
  const [instituteData, setInstituteData] = useState(null);

  // Load data from localStorage on component mount
  useEffect(() => {
    const loadLocalData = () => {
      try {
        const authData = JSON.parse(localStorage.getItem("auth") || "{}");
        const instData = JSON.parse(localStorage.getItem("instituteData") || "{}");
        
        setAuth(authData);
        setInstituteData(instData);
      } catch (error) {
        console.error("Error loading local data:", error);
      }
    };

    loadLocalData();
  }, []);

  // Check if user is institute on component mount
  useEffect(() => {
    if (auth) {
      if (auth?.isAdmin) {
        // If admin, redirect to admin page
        navigate("/ViewSentApplication");
      } else if (!auth?.role) {
        // If not logged in, redirect to login
        navigate("/");
      }
    }
  }, [auth, navigate]);

  // Fetch applications from backend
  const fetchApplications = async () => {
    if (!instituteData?.INST_ID) return;

    try {
      setLoading(true);
      setError("");

      // Only fetch applications for this institute
      const response = await axios.get(`${API_BASE_URL}/applicationsInst`, {
        params: {
          inst_code: instituteData.INST_ID // Filter by institute code
        }
      });
      
      console.log("API Response:", response.data);

      if (response.data.success) {
        const applicationsData = response.data.data || [];
        setApplications(applicationsData);
        setTotalItems(applicationsData.length);
      } else {
        setApplications([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error("Error fetching applications:", err);
      setError(err.response?.data?.message || err.message);

      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (instituteData?.INST_ID) {
      fetchApplications();
    }
  }, [instituteData?.INST_ID]);

  const handleLogout = () => {
    // Clear all auth-related data
    localStorage.removeItem("auth");
    localStorage.removeItem("instituteData");
    localStorage.removeItem("currentView");
    localStorage.removeItem("tempInstituteData");
    localStorage.removeItem("isAdminViewingTemp");
    navigate("/");
  };

  const refreshApplications = () => {
    fetchApplications();
  };

  const getApplicationType = (type) => {
    const types = {
      new_sim: "নতুন ইআইআইএন সিম উত্তোলন",
      eiin_sim_replacement: "পুরাতন ইআইআইএন সিম রিপ্লেসমেন্ট",
      lost_sim_reissue: "হারানো সিম উত্তোলন",
    };
    return types[type] || type;
  };

  const viewApplicationDetails = async (application) => {
    try {
      navigate(`/print/${application.sl_no}`);
    } catch (error) {
      console.error("Error opening PDF:", error);
    }
  };

  // Filter applications based on search term
  const filteredApplications = applications.filter((app) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      app.applicant_name?.toLowerCase().includes(searchLower) ||
      app.inst_name?.toLowerCase().includes(searchLower) ||
      app.inst_code?.toLowerCase().includes(searchLower) ||
      app.contact_number?.includes(searchTerm) ||
      app.mobile_no?.includes(searchTerm)
    );
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentApplications = filteredApplications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Go to next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Go to previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Show loading if no institute data
  if (!instituteData?.INST_ID) {
    return (
      <>
        <Header />
        <div className="container">
          <div className="form-box">
            <div className="loading">লোড হচ্ছে...</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div>
        <div className="container">
          <div className="form-box">
            <h3>
              {instituteData.INST_NAME} - সকল জমা দেয়া আবেদন ({filteredApplications.length})
            </h3>

            {/* Search and Filter Controls */}
            <div className="controls-container" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
                <input
                  type="text"
                  placeholder="সার্চ করুন (নাম, প্রতিষ্ঠান, মোবাইল)..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    minWidth: '250px',
                    flex: 1
                  }}
                />
                <button
                  onClick={refreshApplications}
                  className="btn btn-green"
                  style={{ padding: '8px 16px' }}
                >
                  রিফ্রেশ
                </button>
                <button
                  onClick={() => navigate("/simApp")}
                  className="btn btn-blue"
                  style={{ padding: '8px 16px' }}
                >
                  নতুন আবেদন
                </button>
              </div>

              {/* Items per page selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label>প্রতি পৃষ্ঠায়:</label>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value={5}>৫</option>
                  <option value={10}>১০</option>
                  <option value={20}>২০</option>
                  <option value={50}>৫০</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="loading">লোড হচ্ছে...</div>
            ) : error ? (
              <div className="error-message" style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
                {error}
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="no-data" style={{ textAlign: 'center', padding: '40px' }}>
                <p>কোন আবেদন পাওয়া যায়নি</p>
                <button
                  onClick={() => navigate("/simApp")}
                  className="btn btn-green"
                  style={{ padding: '10px 20px', marginTop: '10px' }}
                >
                  নতুন আবেদন করুন
                </button>
              </div>
            ) : (
              <>
                <div className="applications-table-container" style={{ overflowX: 'auto' }}>
                  <table className="applications-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ padding: '12px', border: '1px solid #dee2e6' }}>ক্রমিক নং</th>
                        <th style={{ padding: '12px', border: '1px solid #dee2e6' }}>আবেদনকারীর নাম</th>
                        <th style={{ padding: '12px', border: '1px solid #dee2e6' }}>পদবী</th>
                        <th style={{ padding: '12px', border: '1px solid #dee2e6' }}>প্রতিষ্ঠান কোড</th>
                        <th style={{ padding: '12px', border: '1px solid #dee2e6' }}>আবেদনের বিষয়</th>
                        <th style={{ padding: '12px', border: '1px solid #dee2e6' }}>প্রতিষ্ঠানের যোগাযোগ নম্বর</th>
                        <th style={{ padding: '12px', border: '1px solid #dee2e6' }}>ব্যাক্তিগত মোবাইল নম্বর</th>
                        <th style={{ padding: '12px', border: '1px solid #dee2e6' }}>পিডিএফ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentApplications.map((app, index) => (
                        <tr key={app.sl_no || index}>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                            {indexOfFirstItem + index + 1}
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{app.applicant_name || "N/A"}</td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{app.designation || "N/A"}</td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{app.inst_code || "N/A"}</td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{getApplicationType(app.application_subject)}</td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{app.contact_number || "N/A"}</td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{app.mobile_no || "N/A"}</td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                            <button onClick={() => viewApplicationDetails(app)}
                              className="btn-view" style={{backgroundColor: '#28a745',
                                color: 'white', border: 'none', padding: '6px 12px',
                                borderRadius: '4px', cursor: 'pointer', fontSize: '13px', minWidth: '70px'
                              }}
                            >
                              পিডিএফ
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="pagination-container" style={{
                    marginTop: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px'
                  }}>
                    <div style={{ color: '#666' }}>
                      দেখানো হচ্ছে {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredApplications.length)} এর মধ্যে {filteredApplications.length} টি আবেদন
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <button
                        onClick={prevPage}
                        disabled={currentPage === 1}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: currentPage === 1 ? '#f5f5f5' : 'white',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          opacity: currentPage === 1 ? 0.6 : 1
                        }}
                      >
                        পূর্ববর্তী
                      </button>
                      
                      {/* Page numbers */}
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => paginate(pageNum)}
                              style={{
                                padding: '6px 12px',
                                border: currentPage === pageNum ? '1px solid #007bff' : '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: currentPage === pageNum ? '#007bff' : 'white',
                                color: currentPage === pageNum ? 'white' : '#333',
                                cursor: 'pointer'
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: currentPage === totalPages ? '#f5f5f5' : 'white',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          opacity: currentPage === totalPages ? 0.6 : 1
                        }}
                      >
                        পরবর্তী
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ViewSentApplicationInst;